import { Tooltip } from '@/components/ui/tooltip'
import { cn } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n'
import { CATEGORY_COLUMN_WIDTH, Z_TIMELINE_HEADER } from '../constants'
import type { SideValues, TimelineColumn, TimelineMonth } from '../types'
import { utilizationDotClass } from '../utils'

interface GanttTimelineHeaderProps {
	months: TimelineMonth[]
	columns: TimelineColumn[]
	/** Utilization percentage per column, keyed by column key. */
	utilization: Record<string, SideValues>
	dayWidth: number
	timelineWidth: number
}

const formatUtilization = (value: number) =>
	Number.isFinite(value) ? `${Math.round(value)}%` : '>100%'

/** One side's utilization: a semaphore dot plus the number, never colour alone. */
function UtilizationRow({ label, value }: { label: string; value: number }) {
	return (
		<span className='flex items-center gap-1 font-normal text-neutral-500'>
			<span
				className={cn(
					'size-1.5 shrink-0 rounded-full',
					utilizationDotClass(value)
				)}
			/>
			{label} {formatUtilization(value)}
		</span>
	)
}

function GanttTimelineHeader({
	months,
	columns,
	utilization,
	dayWidth,
	timelineWidth
}: GanttTimelineHeaderProps) {
	const { t } = useTranslation()

	return (
		<div className='sticky top-0' style={{ zIndex: Z_TIMELINE_HEADER }}>
			<div className='flex'>
				<div
					className='sticky left-0 shrink-0 bg-primary-500'
					style={{
						width: CATEGORY_COLUMN_WIDTH,
						zIndex: Z_TIMELINE_HEADER
					}}
				/>
				<div
					className='relative shrink-0 bg-primary-500'
					style={{ width: timelineWidth, height: 36 }}
				>
					{months.map((month) => (
						<Tooltip
							content={month.tooltip}
							isOpenOnClickEnabled={false}
							key={month.key}
							triggerProps={{ asChild: true }}
						>
							<div
								className='absolute inset-y-0 flex items-center justify-center border-primary-400 border-l font-semibold text-neutral-white text-xs'
								style={{
									left: month.offsetUnits * dayWidth,
									width: month.spanUnits * dayWidth
								}}
							>
								{month.isPadding ? null : (
									<span className='truncate px-1'>
										{month.label}
									</span>
								)}
							</div>
						</Tooltip>
					))}
				</div>
			</div>

			<div className='flex border-neutral-200 border-b'>
				<div
					className='sticky left-0 flex shrink-0 items-center justify-end border-neutral-200 border-r bg-neutral-100 px-3 text-right'
					style={{
						width: CATEGORY_COLUMN_WIDTH,
						zIndex: Z_TIMELINE_HEADER
					}}
				>
					<span className='truncate font-medium text-neutral-500 text-xs'>
						{t('roadmaps:totals_row_label')}
					</span>
				</div>
				<div
					className='relative shrink-0 bg-neutral-100'
					style={{ width: timelineWidth, height: 58 }}
				>
					{columns.map((column) => {
						const util = utilization[column.key] ?? {
							backend: 0,
							frontend: 0
						}
						return (
							<div
								className='absolute inset-y-0 flex flex-col items-center justify-center border-neutral-200 border-l font-semibold text-neutral-700 text-xs'
								key={column.key}
								style={{
									left: column.offsetUnits * dayWidth,
									width: column.spanUnits * dayWidth
								}}
							>
								<span className='truncate px-1'>
									{column.label}
								</span>
								<UtilizationRow
									label='BE'
									value={util.backend}
								/>
								<UtilizationRow
									label='FE'
									value={util.frontend}
								/>
							</div>
						)
					})}
				</div>
			</div>
		</div>
	)
}

export { GanttTimelineHeader }
